/**
 * Document Manager
 * Handles document persistence and change tracking.
 * Extracted from main.js for better separation of concerns.
 *
 * NOTE: CRDT sync orchestration remains in main.js due to tight coupling
 * with auth flow. This module handles local persistence and change tracking.
 */

// Stored callbacks
let callbacks = null;

// Delta sync state
let lastSavedState = null;
let pendingChanges = false;

// Flag to prevent renders during document transitions
let _isLoadingDocument = false;

/**
 * Initialize document manager with callbacks.
 *
 * @param {Object} cbs - Callbacks object with dependencies:
 *   - getState: () => AppState
 *   - get: (key) => any
 *   - saveDocState: (docId, state) => void
 *   - getCurrentUser: () => User|null
 *   - cleanNodeForSync: (node) => CleanedNode
 */
export function init(cbs) {
  callbacks = cbs;
}

/**
 * Save current state to local storage.
 * Saves both local view state and map data.
 */
export async function saveToLocalStorage() {
  if (!callbacks) return;

  const state = callbacks.getState();
  const docId = state.currentDocId;

  if (!docId) {
    // This can happen during app initialization before Store.initialize() is called,
    // or during document transitions. It's safe to skip - the next save will work.
    return;
  }

  // Save local document state (never synced to server)
  // These are per-device UI settings that don't sync via CRDT
  callbacks.saveDocState(docId, {
    mindmapTransform: state.mindmapTransform,
    bubbleTransform: state.bubbleTransform,
    notesPanelOpen: state.notesPanelOpen,
    currentNoteId: state.currentNoteId,
    currentView: state.currentView,
    ganttZoom: state.ganttZoom,
    ganttRowHeight: state.ganttRowHeight,
    ganttScale: state.ganttScale,
    mindmapGap: state.mindmapGap,
    hideCompleted: state.hideCompleted,
    focusId: state.focusId,
    hiddenTagIds: state.hiddenTagIds,
    highlightedTagIds: state.highlightedTagIds,
    tagDimLevel: state.tagDimLevel,
    mindmapLayoutMode: state.mindmapLayoutMode,
    horizon: state.horizon,
  });

  // For shared maps, only save view state locally (data syncs via CRDT)
  if (state.isShared) {
    return;
  }

  // Map data persistence is handled by CRDT (both logged-in and logged-out modes)
  // Here we only mark changes for delta sync when logged in
  if (callbacks.getCurrentUser()) {
    markChanged();
    // Note: Document-level notes use Y.Array as source of truth
    // Do NOT sync state.data.notes to CRDT here - it would overwrite CRDT changes
  }
}

/**
 * Get current state as a JSON string for comparison.
 * Used for delta sync to detect changes.
 */
export function getStateSnapshot() {
  if (!callbacks) return null;

  const state = callbacks.getState();
  if (!state || !state.data) return null;

  return JSON.stringify({
    data: callbacks.cleanNodeForSync(state.data),
    ganttScale: state.ganttScale,
    ganttZoom: state.ganttZoom,
    ganttRowHeight: state.ganttRowHeight,
    mindmapGap: state.mindmapGap,
    mindmapLayoutMode: state.mindmapLayoutMode,
    focusId: state.focusId,
    selectedId: state.selectedId,
    view: state.currentView,
    userSetProjectDates: state.userSetProjectDates,
    hideCompleted: state.hideCompleted,
    notesPanelOpen: state.notesPanelOpen,
  });
}

/**
 * Mark current state as saved (resets pending changes flag).
 */
export function markSaved() {
  lastSavedState = getStateSnapshot();
  pendingChanges = false;
  // Don't update UI indicator - CRDT sync status handles that
}

/**
 * Mark that changes have been made (for beforeunload save).
 */
export function markChanged() {
  pendingChanges = true;
  // Don't update UI indicator - CRDT sync status is the source of truth
}

/**
 * Clear delta sync state (on logout).
 */
export function clearDeltaSyncState() {
  lastSavedState = null;
  pendingChanges = false;
}

/**
 * Check if there are pending unsaved changes.
 */
export function hasPendingChanges() {
  return pendingChanges;
}

/**
 * Get the last saved state snapshot.
 */
export function getLastSavedState() {
  return lastSavedState;
}

/**
 * Check if a document is currently being loaded.
 * Used to prevent renders during document transitions.
 */
export function isDocumentLoading() {
  return _isLoadingDocument;
}

/**
 * Set the document loading flag.
 * @param {boolean} value - Whether a document is being loaded
 */
export function setIsDocumentLoading(value) {
  _isLoadingDocument = value;
}
